import { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import {
  GetWaterProfilesByAccount,
  AlterActiveState,
} from "../../services/ClientWaterProfileService";
import CommonLoader from "../common/CommonLoader";
import PageHeading from "../common/PageHeading";
import SelectOrders from "./SelectOrders"; // Adjust path as needed
import ComparisonArea from "./ComparisonArea"; // Adjust path as needed

import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BalanceIcon from "@mui/icons-material/Balance";
import ShowChartIcon from "@mui/icons-material/ShowChart";

const WaterProfileComparison = ({ accountNumber }) => {
  const columns = [
    { id: "id", label: "ID" },
    { id: "accountNumber", label: "Account" },
    { id: "type", label: "Type" },
    { id: "calcium", label: "Calcium" },
    { id: "totalAlkalinity", label: "Total Alkalinity" },
    { id: "sulfate", label: "Sulfate" },
    { id: "magnesium", label: "Magnesium" },
    { id: "totalHardness", label: "Total Hardness" },
    { id: "pH", label: "pH Level" },
    { id: "totalChlorine", label: "Total Chlorine" },
    { id: "chloride", label: "Chloride" },
    { id: "sodium", label: "Sodium" },
    { id: "tds", label: "TDS" },
    { id: "actions", label: "Actions" },
  ];

  const chemicalFields = [
    "calcium",
    "totalAlkalinity",
    "sulfate",
    "magnesium",
    "totalHardness",
    "pH",
    "totalChlorine",
    "chloride",
    "sodium",
    "tds",
  ];

  const rowsPerPageOptions = [5, 10, 25, 50, 100, 200];

  const [profilesData, setProfilesData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showComparisonArea, setShowComparisonArea] = useState(false);
  const [comparisonData, setComparisonData] = useState({
    profile: null,
    orders: []
  });

  useEffect(() => {
    fetchProfiles();
  }, [accountNumber]);

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    setPaginatedData(profilesData.slice(start, end));
  }, [profilesData, page, rowsPerPage]);

  const fetchProfiles = async () => {
    if (accountNumber == undefined) {
      return;
    }
    try {
      setLoading(true);
      const response = await GetWaterProfilesByAccount(accountNumber);
      setProfilesData(response.results || []);
      setPaginatedData(response.results || []);
    } catch (error) {
      console.error("Error fetching profiles:", error);
      NotificationManager.error("Error fetching profiles", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setShowComparisonArea(false);
    setComparisonData({ profile: null, orders: [] });
    setSelectedProfile(null);
    setSelectedOrders([]);
  };

  const handleToggleActive = async (profileId) => {
    const profile = profilesData.find(p => p.id === profileId);
    if (profile) {
      setSelectedProfile(profile);
      setShowOrderDialog(true);
    }
  };

  const handleOrderSelection = (orderNumbers) => {
    if (selectedProfile && orderNumbers.length > 0) {
      setComparisonData({
        profile: selectedProfile,
        orders: orderNumbers
      });
      setSelectedOrders(orderNumbers);
      setShowOrderDialog(false);
      setShowComparisonArea(true);
      
    }
  };

  const handleCloseOrderDialog = () => {
    setShowOrderDialog(false);
    setSelectedProfile(null);
  };

  const renderActions = (profile) => (
    <div style={{ minWidth: "120px", display: "flex", gap: "4px" }}>
      <Tooltip title="Compare Profile">
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleToggleActive(profile.id)}
        >
          <BalanceIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  // Show comparison area if active
  if (showComparisonArea) {
    return (
      <Box sx={{ p: 2 }}>
        <PageHeading
          title={
            <>
              <BalanceIcon /> Water Profile Comparison
            </>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
            >
              Back to Profiles
            </Button>
          }
        />

        <ComparisonArea
          selectedWaterProfile={comparisonData.profile}
          selectedOrders={comparisonData.orders}
          accountNumber={accountNumber}
          onBack={handleBackClick}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <PageHeading
        title={
          <>
            <ShowChartIcon /> Water Profile Comparison System
          </>
        }
      />

      {selectedOrders.length > 0 && !showComparisonArea && (
        <Alert
          severity="info"
          sx={{ mb: 2, alignItems: "center" }}
          action={
            <Chip
              size="small"
              color="primary"
              label={`${selectedOrders.length} orders`}
            />
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <strong>Last Comparison:</strong>
            {selectedOrders.map((orderNum) => (
              <Chip key={orderNum} size="small" variant="outlined" label={`#${orderNum}`} />
            ))}
          </Box>
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ maxHeight: 600, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    sx={{
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      ...(col.id === "actions" && { minWidth: 130 }),
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((profile) => (
                  <TableRow key={profile.id} hover>
                    {columns.slice(0, -1).map((col) => (
                      <TableCell key={col.id} sx={{ whiteSpace: "nowrap" }}>
                        {chemicalFields.includes(col.id) ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={profile[col.id]?.toFixed(2) || "N/A"}
                          />
                        ) : (
                          profile[col.id] || "N/A"
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{renderActions(profile)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    {loading
                      ? "Loading profiles..."
                      : "No water profiles found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={profilesData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Select Orders Dialog */}
      <SelectOrders
        open={showOrderDialog}
        onClose={handleCloseOrderDialog}
        onDone={handleOrderSelection}
        accountNumber={accountNumber}
        title="Select Orders for Water Profile Comparison"
        maxSelection={10}
      />

      <CommonLoader loading={loading} />
    </Box>
  );
};

export default WaterProfileComparison;
